import { BrainCircuitIcon, XIcon } from "lucide-react";
import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";

import { isMacPlatform } from "../lib/utils";
import {
  ProjectProviderDefaultLabel,
  ProjectProviderFallback,
  type ProjectProviderProps,
  projectProviderReady,
  ProjectProviderRecovery,
} from "./ProjectProviderSection";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Spinner } from "./ui/spinner";
import { Textarea } from "./ui/textarea";

export type ProjectStudyActionResult =
  | { readonly _tag: "Success" }
  | { readonly _tag: "Failure"; readonly message: string };

/**
 * Asks what a project created from references should study, once its first
 * ingest completed. Starting opens a thread that builds on the answer; skipping
 * keeps just the Brain. Either is recorded once.
 */
export function ProjectStudyForm(
  props: ProjectProviderProps & {
    readonly projectTitle: string;
    /** False once the study was skipped: the dialog then only starts one. */
    readonly canSkip: boolean;
    readonly onStart: (study: string) => Promise<ProjectStudyActionResult>;
    readonly onSkip: () => Promise<ProjectStudyActionResult>;
    readonly onClose: () => void;
  },
) {
  const [study, setStudy] = useState("");
  const [pending, setPending] = useState<"start" | "skip" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const activationRef = useRef(false);
  const locked = pending !== null;
  const canStart = !locked && study.trim().length > 0 && projectProviderReady(props);

  const run = async (kind: "start" | "skip") => {
    if (activationRef.current) return;
    activationRef.current = true;
    setPending(kind);
    setErrorMessage(null);
    const result = kind === "start" ? await props.onStart(study) : await props.onSkip();
    if (result._tag === "Failure") {
      activationRef.current = false;
      setPending(null);
      setErrorMessage(result.message);
    }
  };

  if (props.eligibleInstanceIds.size === 0 && !locked) {
    return (
      <ProjectProviderRecovery
        description="A ready Agent Provider is required to start the study."
        onCancel={props.onClose}
        provider={props}
        title="What should this project study?"
      />
    );
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canStart) void run("start");
  };

  return (
    <form className="flex min-h-0 flex-1 flex-col" noValidate onSubmit={onSubmit}>
      <DialogHeader className="pe-14">
        <div className="mb-1 flex items-center gap-1.5 text-muted-foreground text-xs">
          <BrainCircuitIcon className="size-3.5" />
          <span>{props.projectTitle} · references ingested</span>
        </div>
        <DialogTitle>What should this project study?</DialogTitle>
        <DialogDescription>
          Describe the question. A study thread starts from what the Brain now holds, sharpens the
          question with you, then carries it out.
        </DialogDescription>
        <Button
          aria-label="Close"
          className="absolute end-2 top-2"
          disabled={locked}
          onClick={props.onClose}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon />
        </Button>
      </DialogHeader>
      <DialogPanel className="space-y-4">
        <Textarea
          aria-label="What to study"
          autoFocus
          className="min-h-32 resize-y"
          disabled={locked}
          onChange={(event) => setStudy(event.target.value)}
          onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
            const mod = isMacPlatform(navigator.platform) ? event.metaKey : event.ctrlKey;
            if (event.key === "Enter" && mod && !event.nativeEvent.isComposing) {
              event.preventDefault();
              if (canStart) void run("start");
            }
          }}
          placeholder="e.g. How does marine heat stress drive bleaching across these reef sites, and which sites recover?"
          value={study}
        />
        <ProjectProviderFallback locked={locked} provider={props} />
        {errorMessage !== null ? (
          <p
            className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
            role="alert"
          >
            {errorMessage}
          </p>
        ) : null}
      </DialogPanel>
      <DialogFooter className="sm:items-center">
        <ProjectProviderDefaultLabel provider={props} />
        {props.canSkip ? (
          <Button disabled={locked} onClick={() => void run("skip")} type="button" variant="ghost">
            {pending === "skip" ? <Spinner /> : null}
            Skip, just keep the Brain
          </Button>
        ) : (
          <Button disabled={locked} onClick={props.onClose} type="button" variant="outline">
            Cancel
          </Button>
        )}
        <Button disabled={!canStart} type="submit">
          {pending === "start" ? <Spinner /> : null}
          Start the study
        </Button>
      </DialogFooter>
    </form>
  );
}

export function ProjectStudyDialog(
  props: Parameters<typeof ProjectStudyForm>[0] & { readonly open: boolean },
) {
  const { open, ...formProps } = props;
  return (
    <Dialog
      disablePointerDismissal
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      open={open}
    >
      <DialogPopup className="max-w-lg overflow-hidden" showCloseButton={false}>
        {open ? <ProjectStudyForm {...formProps} /> : null}
      </DialogPopup>
    </Dialog>
  );
}
